<template>
  <modal
    v-bind="$attrs"
    v-on="$listeners"
    :close-on-click-modal="false"
    :title="$t('user_profile.change_password.modals.title')"
  >
    <template v-slot:body>
      <p class="help-text">{{ $t('user_profile.change_password.modals.help_text') }}</p>
      <el-form :model="form" ref="changePasswordForm" :rules="rules">
        <elsa-input
          name="password"
          type="password"
          :label="$t('user_profile.change_password.modals.new_password.label')"
          :placeholder="$t('user_profile.change_password.modals.new_password.placeholder')"
          v-model.lazy="form.password"
        />
        <div v-if="passNote.length > 0">
          <label>{{ $t("sign_up.password_field.label") }}</label>
          <ul id="passNote">
            <li v-for="item in passNote" :key="item.message">
              {{ item.message }}
            </li>
          </ul>
        </div>
        <elsa-input
          name="confirmPassword"
          type="password"
          :label="$t('user_profile.change_password.modals.confirm_new_password.label')"
          :placeholder="$t('user_profile.change_password.modals.confirm_new_password.placeholder')"
          v-model.lazy="form.confirmPassword"
        />
      </el-form>
    </template>
    <template v-slot:footer>
      <primary-button
        class="elsa-primary-button"
        @click.native="handleResetPassword"
        ref="submit"
        disabled
        v-loading.fullscreen.lock="fullscreenLoading"
      >
        {{ $t('user_profile.change_password.modals.button_text') }}
      </primary-button>
    </template>
  </modal>
</template>

<script>
  import Modal from '@/components/common/Modal'
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'
  import ElsaInput from '@/components/common/Input'
  import { handleFormValidation, handleSubmitButtonState, isPropEmpty } from '@/helpers/formValidationHelper'
  import { successMessage } from '@/utils/messages';

  export default {
    components: {
      Modal,
      PrimaryButton,
      ElsaInput
    },
    props: {
      currentPassword: {
        type: String,
      },
      email: {
        type: String,
      },
    },
    data() {
      const isValidPwd = (value) => {
        this.passNote = [];
        let isValid = true;
        if (!value.match(/(?=.{8,}$)/)) {
          this.passNote.push({
            message: this.$t('user_profile.change_password.modals.password_validation.long'),
          });
          isValid = false;
        }
        // if (!value.match(/(?=.*[a-z])/)) {
        //   this.passNote.push({
        //     message: this.$t('user_profile.change_password.modals.password_validation.lowercase'),
        //   });
        //   isValid = false;
        // }
        if (!value.match(/(?=.*[A-Z])/)) {
          this.passNote.push({
            message: this.$t('user_profile.change_password.modals.password_validation.uppercase'),
          });
          isValid = false;
        }
        // if (!value.match(/(?=.*[0-9])/)) {
        //   this.passNote.push({
        //     message: this.$t('user_profile.change_password.modals.password_validation.number'),
        //   });
        //   isValid = false;
        // }
        if (!value.match(/(?=.*[0-9(@^!"#$%&'()*+,-./:;<=>?])/)) {
          this.passNote.push({
            message: this.$t('user_profile.change_password.modals.password_validation.number_or_special_char'),
          });
          isValid = false;
        }
        return isValid;
      };
      const validateNewPwd = (rule, value, callback) => {
        const isValid = isValidPwd(value);
        if (!isValid) {
          callback(new Error(this.$t('user_profile.change_password.modals.password_validation.repwd')));
        } else {
          callback();
        }
      };
      const validateConfirmPwd = (rule, value, callback) => {
        const isValid = isValidPwd(this.form.password);
        if (!isValid) {
          callback(new Error(this.$t('user_profile.change_password.modals.password_validation.correctpwd')));
        } else {
          if (this.form.password !== value) {
            callback(new Error(this.$t('user_profile.change_password.modals.password_validation.match')));
          } else {
            callback();
          }
        }
      };
      return {
        form: {},
        fullscreenLoading: false,
        passNote: [],
        rules: {
          password: [
            {
              required: true,
              message: this.$t('user_profile.change_password.modals.password_validation.please_input'),
              trigger: ["blur", "change"],
            },
            { validator: validateNewPwd, trigger: ["blur", "change"] },
          ],
          confirmPassword: [
            {
              required: true,
              message: this.$t('user_profile.change_password.modals.password_validation.input_confirm'),
              trigger: ["blur", "change"],
            },
            { validator: validateConfirmPwd, trigger: ["blur", "change"] },
          ],
        }
      }
    },
    methods: {
      showError(e) {
        return e;
      },
      handleResetPassword() {
        this.fullscreenLoading = true
        const data = {
          email: this.email,
          password: this.currentPassword,
          new_password: this.form.password
        }

        return handleFormValidation(this.$refs.changePasswordForm, () => {
            return this.$axios.put(`/v2/accounts/change-password`, data)
            .then(response => this.$store.dispatch('' , response.data))
            .then(() => {
              this.fullscreenLoading = false

              successMessage(this.$t('user_profile.change_password.message'), 'elsa-message success', {
                showClose: false
              })(this)

              this.$emit('closed')
            })
          },
          this.showError
        )
      },
      onFormChange() {
        const isValid = isPropEmpty("password")(this.form) || isPropEmpty("confirmPassword")(this.form)
        handleSubmitButtonState(this.$refs.submit.$el, !isValid)
      }
    },
    watch: {
      form() {
        return this.onFormChange()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .elsa-button {
    width: 202px;
  }

  .elsa-primary-button {
    margin-top: 7px;
    width: 100%;
  }

  h2 {
    margin-top: 8px;
    margin-bottom: 48px;
  }

  #passNote {
    color: #f56c6c;
  }

  ::v-deep .el-dialog__body {
    padding: 0 20px;
  }

  ::v-deep .el-dialog.elsa-modal .el-dialog__body > *:not(p):not(.info) {
    padding-bottom: 0;
  }
  ::v-deep .el-form .el-form-item .el-form-item__label {
    text-transform: none !important;
  }
</style>
